<script setup>
import { onMounted, ref } from 'vue';
import { useBreakpoints } from '@vueuse/core';
import { computed } from 'vue';
import { PollingManager } from '@/utils/PollingManager';
import { inject } from 'vue';
import BottomSidebar from './BottomSidebar/BottomSidebar.vue';
import { safeParsePostMessage, createPostMessageChannel } from '@/utils/postMessageHelpers';
import { getItemId, getPathUserId } from '@/utils/auth';
import { captureException } from '@sentry/vue';
import GradientLoader from './GradientLoader.vue';

const COLLABORA_ONLINE_URL = import.meta.env.VITE_COLLABORA_ONLINE_URL;
const WOPI_URL = import.meta.env.VITE_WOPI_URL;
const BACKEND_URL = import.meta.env.VITE_BACKEND_URL;
const SERVE_PPTX_API = import.meta.env.VITE_SERVE_PPTX_API;

const props = defineProps(['presentationId']);

const emit = defineEmits(['toggle-loading', 'require-auth']);

const currentSlideNumber = ref(1);

var messages = [];

const iframe = ref(null);
const pollingManager = ref(null);
const totalSlideInsertsCount = ref(0);
const slideInserts = ref({});
const isUserAuthenticated = ref(false);

const { setEditorIframeRef } = inject('editorIframeRef');
const userRef = inject('userRef');
const isMobileRef = inject('isMobileRef');
const isDocumentLoadedRef = inject('isDocumentLoadedRef');

const breakpoints = useBreakpoints({
    xs: 0,
    sm: 1280,
    md: 1440,
    lg: 1680,
    xl: 1920,
})

const showInput = ref(false);
const selectedText = ref('');
const inputText = ref('');
const isLoading = ref(false);
const isSpinnerLoading = ref(false);

function handleClickOutside() {
    if (!isLoading.value) {
        showInput.value = false;
    }
};

const currentBreakpoint = computed(() => {
    if (breakpoints.smaller('sm').value) return 'xs'
    if (breakpoints.between('sm', 'md').value) return 'sm'
    if (breakpoints.between('md', 'lg').value) return 'md'
    if (breakpoints.between('lg', 'xl').value) return 'lg'
    if (breakpoints.greaterOrEqual('xl').value) return 'xl'
    return 'unknown';
});

onMounted(async () => {
    const pathUserId = getPathUserId(props.presentationId);
    if (userRef.value.id) {
        isUserAuthenticated.value = true;
    }

    const request = new Request(BACKEND_URL + "/hosting/discovery");
    fetch(request)
        .then((response) => {
            if (!response.ok) {
                throw new Error(`HTTP error! Status: ${response.status}`);
            }

            return response.text();
        })
        .then((data) => {
            const parser = new DOMParser();
            const xmlText = parser.parseFromString(data, "text/xml");
            const iframeUrl = xmlText.querySelector('app[name=impress] > action[ext=odp]').getAttribute('urlsrc')
                + `WOPISrc=${WOPI_URL}/wopi/files/${props.presentationId}`;
            var form = document.getElementById("form");
            form.action = iframeUrl;
            form.submit();
        });
    window.addEventListener('message', handlePostMessage, false);
    setEditorIframeRef(iframe.value);
});

function setEditorZoomLevelFromBreakpoint(breakpoint) {
    var zoomLevel = 10;
    if (!isMobileRef.value) {
        switch (breakpoint) {
            case 'xs':
                zoomLevel = 7;
                break;
            case 'sm':
                zoomLevel = 8;
                break;
            case 'md':
                zoomLevel = 9;
                break;
            case 'lg':
                zoomLevel = 10;
                break;
            case 'xl':
                zoomLevel = 11;
                break;
        }
        document.getElementById("coolframe").contentWindow.postMessage(JSON.stringify({
            "MessageId": "Set_Zoom",
            "SendTime": Date.now(),
            "Values": {
                "Level": zoomLevel
            }
        }), COLLABORA_ONLINE_URL);
    }
}

function handlePostMessage(e) {
    var msg = safeParsePostMessage(e.data);
    if (
        msg.MessageId === "CallPythonScript-Result"
        && msg.Values.commandName.startsWith("vnd.sun.star.script:ReplaceAllText.py$GetAllText")
    ) {
        const slideData = JSON.parse(msg.Values.result.value);
        const jsonData = {
            "chatbot_data": {
                "messages": messages
            },
            "slide_data": slideData.data
        };
        const request = new Request('https://eprid4tv0b.execute-api.eu-west-1.amazonaws.com/final/replacer', {
            method: "POST",
            body: JSON.stringify(jsonData),
        });
        fetch(request)
            .then((response) => {
                if (!response.ok) {
                    throw new Error(`HTTP error! Status: ${response.status}`);
                }
                return response.text();
            })
            .then((data) => {
                data = JSON.parse(data);
                const replacementTextData = data.body;
                document.getElementById("coolframe").contentWindow.postMessage(JSON.stringify({
                    "MessageId": "Send_UNO_Command",
                    "SendTime": Date.now(),
                    "Values": { "Command": ".uno:DuplicatePage", "Args": {} }
                }), COLLABORA_ONLINE_URL);
                setTimeout(function () {
                    document.getElementById("coolframe").contentWindow.postMessage(JSON.stringify({
                        "MessageId": "CallPythonScript",
                        "SendTime": Date.now(),
                        "ScriptFile": "ReplaceAllText.py",
                        "Function": "ReplaceAllText",
                        "Values": {
                            "data": { "type": "string", "value": JSON.stringify(replacementTextData) }
                        }
                    }), COLLABORA_ONLINE_URL);
                }, 1000);
            });
    } else if (
        msg.MessageId === "CallPythonScript-Result"
        && msg.Values.commandName.startsWith("vnd.sun.star.script:ReplaceAllText.py$ReplaceAllText")
    ) {
        document.getElementById("coolframe").contentWindow.postMessage(JSON.stringify({
            "MessageId": "CallPythonScript",
            "SendTime": Date.now(),
            "ScriptFile": "ReplaceAllText.py",
            "Function": "HidePreviousSlide",
            "Values": {}
        }), COLLABORA_ONLINE_URL);
        document.getElementById("coolframe").contentWindow.postMessage(JSON.stringify({
            "MessageId": "Action_Manual_Reconnect",
            "SendTime": Date.now(),
            "Values": {}
        }), COLLABORA_ONLINE_URL);
        setTimeout(function () {
            emit("toggle-loading", false);
        }, 1000);
    } else if (msg.MessageId === "RequestContentChange") {
        emit("toggle-loading", true);
        document.getElementById("coolframe").contentWindow.postMessage(JSON.stringify({
            "MessageId": "CallPythonScript",
            "SendTime": Date.now(),
            "ScriptFile": "ReplaceAllText.py",
            "Function": "GetAllText",
            "Values": {}
        }), COLLABORA_ONLINE_URL);
        messages = msg.Values.messages.value;
    } else if (msg.MessageId === "InsertImage") {
        insertImage(msg.Values.imageUrl.value);
    } else if (msg.MessageId === "Action_Export") {
        document.getElementById("coolframe").contentWindow.postMessage(JSON.stringify(msg), COLLABORA_ONLINE_URL);
    } else if (msg.MessageId === "Page_Changed") {
        currentSlideNumber.value = parseInt(msg.Values.PageNumber);
        if (currentSlideNumber.value > 3 && !isUserAuthenticated.value) {
            emit('require-auth');
        }
    } else if (
        msg.MessageId === "CallPythonScript-Result"
        && msg.Values.commandName.startsWith("vnd.sun.star.script:ReplaceSelectedText.py$GetSelectedTextForExpanding")
    ) {
        isSpinnerLoading.value = true;
        const selectedText = msg.Values.result.value;
        const jsonData = {
            "chatbot_data": {
                "messages": [{ "author": "user", "content": "the content should be expanded", "id": 0 }]
            },
            "slide_data": [{ "id": 0, "text_portion_id": 0, "text": selectedText }]
        };
        const request = new Request('https://eprid4tv0b.execute-api.eu-west-1.amazonaws.com/final/replacer', {
            method: "POST",
            body: JSON.stringify(jsonData),
        });
        fetch(request)
            .then((response) => {
                if (!response.ok) {
                    throw new Error(`HTTP error! Status: ${response.status}`);
                }
                return response.text();
            })
            .then((data) => {
                data = JSON.parse(data);
                const replacementTextData = data.body;
                console.log(JSON.stringify(replacementTextData));
                setTimeout(function () {
                    document.getElementById("coolframe").contentWindow.postMessage(JSON.stringify({
                        "MessageId": "CallPythonScript",
                        "SendTime": Date.now(),
                        "ScriptFile": "ReplaceSelectedText.py",
                        "Function": "ReplaceSelectedTextWithExpandedText",
                        "Values": {
                            "data": { "type": "string", "value": replacementTextData.data[0].replacement_text }
                        }
                    }), COLLABORA_ONLINE_URL);
                }, 1000);
            });
    } else if (
        msg.MessageId === "CallPythonScript-Result"
        && msg.Values.commandName.startsWith("vnd.sun.star.script:ReplaceSelectedText.py$GetSelectedTextForFixingGrammar")
    ) {
        isSpinnerLoading.value = true;
        const selectedText = msg.Values.result.value;
        const jsonData = {
            "chatbot_data": {
                "messages": [{ "author": "user", "content": "the content's grammar should be fixed", "id": 0 }]
            },
            "slide_data": [{ "id": 0, "text_portion_id": 0, "text": selectedText }]
        };
        const request = new Request('https://eprid4tv0b.execute-api.eu-west-1.amazonaws.com/final/replacer', {
            method: "POST",
            body: JSON.stringify(jsonData),
        });
        fetch(request)
            .then((response) => {
                if (!response.ok) {
                    throw new Error(`HTTP error! Status: ${response.status}`);
                }
                return response.text();
            })
            .then((data) => {
                data = JSON.parse(data);
                const replacementTextData = data.body;
                console.log(JSON.stringify(replacementTextData));
                setTimeout(function () {
                    document.getElementById("coolframe").contentWindow.postMessage(JSON.stringify({
                        "MessageId": "CallPythonScript",
                        "SendTime": Date.now(),
                        "ScriptFile": "ReplaceSelectedText.py",
                        "Function": "ReplaceSelectedTextWithFixedGrammarText",
                        "Values": {
                            "data": { "type": "string", "value": replacementTextData.data[0].replacement_text }
                        }
                    }), COLLABORA_ONLINE_URL);
                }, 1000);
            });
    } else if (
        msg.MessageId === "CallPythonScript-Result"
        && msg.Values.commandName.startsWith("vnd.sun.star.script:ReplaceSelectedText.py$GetSelectedTextForShortening")
    ) {
        isSpinnerLoading.value = true;
        const selectedText = msg.Values.result.value;
        const jsonData = {
            "chatbot_data": {
                "messages": [{ "author": "user", "content": "the content should be shortened", "id": 0 }]
            },
            "slide_data": [{ "id": 0, "text_portion_id": 0, "text": selectedText }]
        };
        const request = new Request('https://eprid4tv0b.execute-api.eu-west-1.amazonaws.com/final/replacer', {
            method: "POST",
            body: JSON.stringify(jsonData),
        });
        fetch(request)
            .then((response) => {
                if (!response.ok) {
                    throw new Error(`HTTP error! Status: ${response.status}`);
                }
                return response.text();
            })
            .then((data) => {
                data = JSON.parse(data);
                const replacementTextData = data.body;
                console.log(JSON.stringify(replacementTextData));
                setTimeout(function () {
                    document.getElementById("coolframe").contentWindow.postMessage(JSON.stringify({
                        "MessageId": "CallPythonScript",
                        "SendTime": Date.now(),
                        "ScriptFile": "ReplaceSelectedText.py",
                        "Function": "ReplaceSelectedTextWithShortenedText",
                        "Values": {
                            "data": { "type": "string", "value": replacementTextData.data[0].replacement_text }
                        }
                    }), COLLABORA_ONLINE_URL);
                }, 1000);
            });
    } else if (
        msg.MessageId === "CallPythonScript-Result"
        && msg.Values.commandName.startsWith("vnd.sun.star.script:ReplaceSelectedText.py$GetSelectedTextForRewriteingIntoBullets")
    ) {
        isSpinnerLoading.value = true;
        const selectedText = msg.Values.result.value;
        const jsonData = {
            "chatbot_data": {
                "messages": [{ "author": "user", "content": "the content should be rephrased into text bullets, give me the response such that I can parse it into a json array", "id": 0 }]
            },
            "slide_data": [{ "id": 0, "text_portion_id": 0, "text": selectedText }]
        };
        const request = new Request('https://eprid4tv0b.execute-api.eu-west-1.amazonaws.com/final/replacer', {
            method: "POST",
            body: JSON.stringify(jsonData),
        });
        fetch(request)
            .then((response) => {
                if (!response.ok) {
                    throw new Error(`HTTP error! Status: ${response.status}`);
                }
                return response.text();
            })
            .then((data) => {
                data = JSON.parse(data);
                const replacementTextData = data.body;
                console.log(replacementTextData);
                setTimeout(function () {
                    document.getElementById("coolframe").contentWindow.postMessage(JSON.stringify({
                        "MessageId": "CallPythonScript",
                        "SendTime": Date.now(),
                        "ScriptFile": "ReplaceSelectedText.py",
                        "Function": "ReplaceSelectedTextWithTextBullets",
                        "Values": {
                            "data": { "type": "string", "value": JSON.stringify(replacementTextData.data[0].replacement_text) }
                        }
                    }), COLLABORA_ONLINE_URL);
                }, 1000);
            });
    } else if (
        msg.MessageId === "CallPythonScript-Result"
        && msg.Values.commandName.startsWith("vnd.sun.star.script:ReplaceSelectedText.py$GetSelectedTextForRewritingWithCustomInstruction")
    ) {
        showInput.value = true;
        selectedText.value = msg.Values.result.value;
    } else if (
        msg.MessageId === "CallPythonScript-Result"
        && msg.Values.commandName.startsWith("vnd.sun.star.script:ReplaceSelectedText.py$ReplaceSelectedTextWithShortenedText")
    ) {
        isSpinnerLoading.value = false;
    } else if (
        msg.MessageId === "CallPythonScript-Result"
        && msg.Values.commandName.startsWith("vnd.sun.star.script:ReplaceSelectedText.py$ReplaceSelectedTextWithExpandedText")
    ) {
        isSpinnerLoading.value = false;
    } else if (
        msg.MessageId === "CallPythonScript-Result"
        && msg.Values.commandName.startsWith("vnd.sun.star.script:ReplaceSelectedText.py$ReplaceSelectedTextWithFixedGrammarText")
    ) {
        isSpinnerLoading.value = false;
    } else if (
        msg.MessageId === "CallPythonScript-Result"
        && msg.Values.commandName.startsWith("vnd.sun.star.script:ReplaceSelectedText.py$ReplaceSelectedTextWithTextBullets")
    ) {
        isSpinnerLoading.value = false;
    } else if (
        msg.MessageId === "CallPythonScript-Result"
        && msg.Values.commandName.startsWith("vnd.sun.star.script:ReplaceSelectedText.py$ReplaceSelectedTextThroughCustomInstruction")
    ) {
        showInput.value = false;
        isLoading.value = false;
        inputText.value = '';
    } else if (
        msg.MessageId === "App_LoadingStatus"
        && msg.Values.Status === "Document_Loaded"
    ) {
        isDocumentLoadedRef.value = true;
        document.getElementById("coolframe").contentWindow.postMessage(JSON.stringify({
            "MessageId": "Host_PostmessageReady",
            "SendTime": Date.now(),
            "Values": {}
        }), COLLABORA_ONLINE_URL);
        initEditor();
        initLoadExtraSlides({});
    } else {
        console.log(msg);
    }
}

async function initEditor() {
    isMobileRef.value = await isMobile();
    console.log(isMobileRef.value);
    setEditorZoomLevelFromBreakpoint(currentBreakpoint.value);
}

function handleRewriteTextWithCustomInstruction() {
    isLoading.value = true;
    const jsonData = {
        "chatbot_data": {
            "messages": [{ "author": "user", "content": inputText.value, "id": 0 }]
        },
        "slide_data": [{ "id": 0, "text_portion_id": 0, "text": selectedText }]
    };
    const request = new Request('https://eprid4tv0b.execute-api.eu-west-1.amazonaws.com/final/replacer', {
        method: "POST",
        body: JSON.stringify(jsonData),
    });
    fetch(request)
        .then((response) => {
            if (!response.ok) {
                throw new Error(`HTTP error! Status: ${response.status}`);
            }
            return response.text();
        })
        .then((data) => {
            data = JSON.parse(data);
            const replacementTextData = data.body;
            setTimeout(function () {
                document.getElementById("coolframe").contentWindow.postMessage(JSON.stringify({
                    "MessageId": "CallPythonScript",
                    "SendTime": Date.now(),
                    "ScriptFile": "ReplaceSelectedText.py",
                    "Function": "ReplaceSelectedTextThroughCustomInstruction",
                    "Values": {
                        "data": { "type": "string", "value": replacementTextData.data[0].replacement_text }
                    }
                }), COLLABORA_ONLINE_URL);
            }, 1000);
        });
}

function insertImage(imageUrl) {
    document.getElementById("coolframe").contentWindow.postMessage(JSON.stringify({
        "MessageId": "CallPythonScript",
        "SendTime": Date.now(),
        "ScriptFile": "InsertImage.py",
        "Function": "AddImageFromUrl",
        "Values": {
            "url": { "type": "string", "value": imageUrl }
        }
    }), COLLABORA_ONLINE_URL);
}

async function initLoadExtraSlides(options) {
    slideInserts.value[1] = { slide_number: 1, last_inserted_slide_name: null };

    let apiIsDone = false;
    let slidesCount = await countSlides();
    console.log(`Number of slides is ${slidesCount}`);

    let url = SERVE_PPTX_API;
    /* let url = "http://172.17.0.1:8000/api/slide-status/"; */
    /* let url = "https://editor.heiwiper.com/api/slide-status/"; */

    try {
        const response = await fetch(url, {
            method: 'POST',
            body: JSON.stringify({ "item_id": getItemId(props.presentationId) })
        });
        const data = await response.json();
        const items = JSON.parse(data.body).slides;
        if (items.length > 0 && items[items.length - 1].done) {
            apiIsDone = true;
        }
        if (slidesCount == 1 || !apiIsDone) {
            // we don't count last item that should indicates status
            totalSlideInsertsCount.value = items.length - 1;
            // we don't count first item that is already loaded
            setLoadingSlides(totalSlideInsertsCount.value - Object.keys(slideInserts.value).length);

            pollingManager.value = new PollingManager(
                url,
                getItemId(props.presentationId),
                updateItem.bind(this),
                sendMessageToIframe.bind(this)
            );

            pollingManager.value.startPolling();
        }
    } catch (error) {
        console.error('Polling error:', error);
        captureException(error, { level: "error" });
        return;
    }
}

const setLoadingSlides = (targetNumber) => {
    console.log("INSERTING LOADING SLIDES " + targetNumber);
    const iframeWindow = iframe.value.contentWindow;
    iframeWindow.postMessage(JSON.stringify({
        "MessageId": "Set_Loading_Placeholder_Slides",
        "SendTime": Date.now(),
        "Values": {
            "Number": targetNumber
        }
    }), COLLABORA_ONLINE_URL);
};

const updateItem = (index, item, lastInsertedSlideName) => {
    slideInserts.value[item.slide_number] = {
        slide_number: item.slide_number,
        presigned_url: item.presigned_url,
        status: 'succeeded',
        last_inserted_slide_name: lastInsertedSlideName
    };
    setLoadingSlides(totalSlideInsertsCount.value - Object.keys(slideInserts.value).length);
    console.log("Updating item...");
    console.log(slideInserts.value[item.slide_number]);
};

// Send message to iframe
const countSlides = async () => {
    const response = await createPostMessageChannel(
        iframe.value.contentWindow,
        COLLABORA_ONLINE_URL,
        JSON.stringify({
            "MessageId": "Count_Slides",
            "SendTime": Date.now(),
            "Values": {}
        }),
        (msg) => msg.MessageId === 'Count_Slides_Resp',
        {
            maxRetries: 3
        }
    );
    const slidesCount = response.Values.slidesCount;
    return slidesCount;
};

// Send message to iframe
const sendMessageToIframe = async (item, requestId, retry = false) => {
    return new Promise((resolve) => {
        let timeoutId;
        const listener = (event) => {
            var msg = safeParsePostMessage(event.data);
            if (
                msg.MessageId === 'CallPythonScript-Result'
                && (msg.Values.commandName.startsWith("vnd.sun.star.script:InsertMultipleSlides.py$AddSlidesFromPresignedUrlsV3"))
            ) {
                cleanup();
                const result = JSON.parse(msg.Values.result.value);
                resolve(result.last_inserted_name);
            }
        };

        const cleanup = () => {
            window.removeEventListener('message', listener);
            if (timeoutId) {
                clearTimeout(timeoutId);
            }
        }

        timeoutId = setTimeout(() => {
            cleanup();
            console.log('Timeout reached for listener, resolving with null');
            resolve(null);
        }, 10000);

        const iframeWindow = iframe.value.contentWindow;
        const messageValues = {
            "presigned_urls_json": { "type": "string", "value": `[${JSON.stringify(item.presigned_url)}]` },
            "slide_number": { "type": "long", "value": item.slide_number },
        };
        iframeWindow.postMessage(JSON.stringify({
            "MessageId": "CallPythonScript",
            "SendTime": Date.now(),
            "ScriptFile": "InsertMultipleSlides.py",
            "Function": "AddSlidesFromPresignedUrlsV3",
            "Values": messageValues
        }), COLLABORA_ONLINE_URL);

        window.addEventListener('message', listener);
    });
};

/**
 * Finds the previous existing element in an associative array
 * @param {Object} associativeArray - The associative array (object) to search
 * @param {number|string} currentKey - The current key to start searching from
 * @returns {*} The value of the previous element or first element if none found
 */
function findPreviousElement(associativeArray, currentKey) {
    associativeArray[currentKey] = {};
    // Convert currentKey to number if it's a numeric string
    const key = isNaN(currentKey) ? currentKey : Number(currentKey);

    // If the input isn't an object or is empty, return undefined
    if (typeof associativeArray !== 'object' || associativeArray === null || Array.isArray(associativeArray)) {
        return undefined;
    }

    // Get all existing keys sorted numerically
    const keys = Object.keys(associativeArray)
        .map(k => isNaN(k) ? k : Number(k))
        .sort((a, b) => a - b);
    console.log("Finding previous element...");
    console.log(currentKey);
    console.log(keys);
    Object.keys(associativeArray).forEach(key => {
        console.log(`${key}: ${JSON.stringify(associativeArray[key])}`);
    });

    // If no keys exist, return undefined
    if (keys.length === 0) return undefined;

    // If currentKey doesn't exist in the object, return the last element
    if (!keys.includes(key)) {
        return associativeArray[keys[keys.length - 1]];
    }

    // Find the index of current key
    const currentIndex = keys.indexOf(key);

    // If it's the first key, return it (no previous element)
    if (currentIndex === 0) {
        return associativeArray[keys[0]];
    }

    // Otherwise return the previous element
    return associativeArray[keys[currentIndex - 1]];
}

// Send message to iframe
const isMobile = async () => {
    const response = await createPostMessageChannel(
        document.getElementById("coolframe").contentWindow,
        COLLABORA_ONLINE_URL,
        JSON.stringify({
            "MessageId": "Is_Mobile",
            "SendTime": Date.now(),
            "Values": {}
        }),
        (msg) => {
            return msg.MessageId === 'Is_Mobile_Resp';
        },
    );
    return response.Values.IsMobile;
};

</script>

<template>
    <form id="form" action enctype="multipart/form-data" target="coolframe" method="post">
        <input name="access_token" :value="userRef.id" type="hidden" />
    </form>
    <iframe ref="iframe" id="coolframe" name="coolframe" style="width:100%;height:100%;position:absolute;">
    </iframe>
    <div class="is-overlay" :class="{ 'is-hidden': !showInput }" @click="handleClickOutside()" style="z-index: 200;">
    </div>
    <div class="input-container" :class="{ show: showInput, loading: isLoading }" style="z-index: 300;">
        <div class="gradient-border">
            <input v-model="inputText" type="text" placeholder="Enter text..." :disabled="isLoading"
                class="styled-input" />
            <button @click="handleRewriteTextWithCustomInstruction"
                @keyup.enter="handleRewriteTextWithCustomInstruction" :disabled="!inputText || isLoading"
                class="submit-button">
                <span v-if="!isLoading">Submit</span>
                <div v-else class="spinner"></div>
            </button>
        </div>
    </div>
    <BottomSidebar v-if="!isMobileRef" :presentationId="presentationId" :currentSlideNumber="currentSlideNumber" />
    <GradientLoader :loading="isSpinnerLoading" />
</template>

<style>
iframe {
    margin: 0px;
    padding: 0px;
    border: none;
}

.input-container {
    position: fixed;
    bottom: -100px;
    left: 50%;
    transform: translateX(-50%);
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    width: 90%;
    max-width: 500px;
}

.input-container.show {
    bottom: 20px;
}

.gradient-border {
    padding: 3px;
    background: linear-gradient(135deg, #7F00FF, #E100FF);
    border-radius: 12px;
    box-shadow: 0 4px 15px rgba(138, 43, 226, 0.3);
}

.styled-input {
    width: 100%;
    padding: 15px 20px;
    border: none;
    border-radius: 10px;
    font-size: 16px;
    background: #fff;
    outline: none;
}

.submit-button {
    position: absolute;
    right: 10px;
    top: 50%;
    transform: translateY(-50%);
    background: linear-gradient(135deg, #7F00FF, #E100FF);
    color: white;
    border: none;
    padding: 8px 20px;
    border-radius: 8px;
    cursor: pointer;
    transition: opacity 0.2s;
}

.submit-button:disabled {
    opacity: 0.7;
    cursor: not-allowed;
}

.spinner {
    width: 20px;
    height: 20px;
    border: 3px solid rgba(255, 255, 255, 0.3);
    border-top-color: white;
    border-radius: 50%;
    animation: spin 1s linear infinite;
}

@keyframes spin {
    to {
        transform: rotate(360deg);
    }
}

.trigger-button {
    background: linear-gradient(135deg, #7F00FF, #E100FF);
    color: white;
    border: none;
    padding: 12px 24px;
    border-radius: 8px;
    cursor: pointer;
    margin: 20px;
    box-shadow: 0 4px 15px rgba(138, 43, 226, 0.3);
}

.loading .gradient-border {
    background: linear-gradient(135deg, #E100FF, #7F00FF);
    animation: gradient-pulse 1.5s ease infinite;
}

@keyframes gradient-pulse {
    0% {
        opacity: 1;
    }

    50% {
        opacity: 0.8;
    }

    100% {
        opacity: 1;
    }
}
</style>
